import { useMemo, useRef, useCallback, useState, useEffect } from 'react';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { cn } from '../lib/utils';
import { getWeeksBetween, getWeekDateRange, getWeekKey, hoursToWorkingDays, DEFAULT_START_DATE, DEFAULT_END_DATE } from '../utils/dateUtils';
import { eachDayOfInterval, format } from 'date-fns';

const ROW_HEIGHT = 56;
const CELL_WIDTH = 110;
const SIDEBAR_WIDTH = 200;
const SIDEBAR_MIN_WIDTH = 160;
const SIDEBAR_MAX_WIDTH = 520;
const MAX_WORKING_DAYS = 5;

export const ResourceGrid = ({ resources, tasks, projects, holidays, startDate, endDate, onResourceClick }) => {
  const gridStartDate = startDate || DEFAULT_START_DATE;
  const gridEndDate = endDate || DEFAULT_END_DATE;
  const sidebarScrollRef = useRef(null);
  const headerScrollRef = useRef(null);
  const gridScrollRef = useRef(null);
  const isSyncingTopRef = useRef(false);
  const isSyncingLeftRef = useRef(false);
  const [sidebarWidth, setSidebarWidth] = useState(SIDEBAR_WIDTH);
  const isResizingRef = useRef(false);
  const resizeStartXRef = useRef(0);
  const resizeStartWidthRef = useRef(SIDEBAR_WIDTH);

  const syncScrollTop = useCallback(function(sourceRef, targetRef) {
    if (!sourceRef.current || !targetRef.current) return;
    if (isSyncingTopRef.current) return;
    isSyncingTopRef.current = true;
    targetRef.current.scrollTop = sourceRef.current.scrollTop;
    requestAnimationFrame(function() {
      isSyncingTopRef.current = false;
    });
  }, []);

  const syncScrollLeft = useCallback(function(sourceRef, targetRef) {
    if (!sourceRef.current || !targetRef.current) return;
    if (isSyncingLeftRef.current) return;
    isSyncingLeftRef.current = true;
    targetRef.current.scrollLeft = sourceRef.current.scrollLeft;
    requestAnimationFrame(function() {
      isSyncingLeftRef.current = false;
    });
  }, []);

  const handleResizing = useCallback(function(e) {
    if (!isResizingRef.current) return;
    const delta = e.clientX - resizeStartXRef.current;
    const nextWidth = Math.min(SIDEBAR_MAX_WIDTH, Math.max(SIDEBAR_MIN_WIDTH, resizeStartWidthRef.current + delta));
    setSidebarWidth(nextWidth);
  }, []);

  const handleResizeEnd = useCallback(function() {
    if (!isResizingRef.current) return;
    isResizingRef.current = false;
    window.removeEventListener('mousemove', handleResizing);
    window.removeEventListener('mouseup', handleResizeEnd);
  }, [handleResizing]);

  const handleResizeStart = useCallback(function(e) {
    e.preventDefault();
    isResizingRef.current = true;
    resizeStartXRef.current = e.clientX;
    resizeStartWidthRef.current = sidebarWidth;
    window.addEventListener('mousemove', handleResizing);
    window.addEventListener('mouseup', handleResizeEnd);
  }, [handleResizing, handleResizeEnd, sidebarWidth]);

  useEffect(function() {
    return function() {
      window.removeEventListener('mousemove', handleResizing);
      window.removeEventListener('mouseup', handleResizeEnd);
    };
  }, [handleResizing, handleResizeEnd]);

  const weeks = useMemo(function() {
    return getWeeksBetween(gridStartDate, gridEndDate);
  }, [gridStartDate, gridEndDate]);

  const sortedResources = useMemo(function() {
    const copy = resources.slice();
    copy.sort(function(a, b) {
      const aName = (a.name || '').trim();
      const bName = (b.name || '').trim();
      const aParts = aName.split(/\s+/).filter(Boolean);
      const bParts = bName.split(/\s+/).filter(Boolean);
      const aLast = aParts.length ? aParts[aParts.length - 1] : '';
      const bLast = bParts.length ? bParts[bParts.length - 1] : '';
      const lastCmp = aLast.localeCompare(bLast, 'it', { sensitivity: 'base' });
      if (lastCmp !== 0) return lastCmp;
      return aName.localeCompare(bName, 'it', { sensitivity: 'base' });
    });
    return copy;
  }, [resources]);

  const currentWeekIndex = useMemo(function() {
    const todayKey = getWeekKey(new Date());
    const index = weeks.indexOf(todayKey);
    return index >= 0 ? index : null;
  }, [weeks]);

  // Pre-compute holiday hours per resource per week
  const holidayData = useMemo(function() {
    const data = {};
    const allHolidays = holidays || {};
    const resourceIds = Object.keys(allHolidays);
    for (let ri = 0; ri < resourceIds.length; ri++) {
      const resourceId = resourceIds[ri];
      const resourceHolidays = allHolidays[resourceId];
      data[resourceId] = {};
      for (let wi = 0; wi < weeks.length; wi++) {
        const weekKey = weeks[wi];
        // Get Monday of the week
        const parts = weekKey.split('-W');
        const year = parseInt(parts[0]);
        const weekNum = parseInt(parts[1]);
        const jan4 = new Date(year, 0, 4);
        const jan4Day = jan4.getDay();
        const jan4Monday = new Date(year, 0, 4 - (jan4Day === 0 ? 6 : jan4Day - 1));
        const weekStart = new Date(jan4Monday);
        weekStart.setDate(jan4Monday.getDate() + (weekNum - 1) * 7);
        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekStart.getDate() + 6);
        // Count vacation days in this week
        let holidayHours = 0;
        const days = eachDayOfInterval({ start: weekStart, end: weekEnd });
        for (let d = 0; d < days.length; d++) {
          const dateStr = format(days[d], 'yyyy-MM-dd');
          if (resourceHolidays[dateStr]) {
            holidayHours += resourceHolidays[dateStr];
          }
        }
        data[resourceId][weekKey] = holidayHours;
      }
    }
    return data;
  }, [holidays, weeks]);

  const workloadData = useMemo(function() {
    const data = {};
    const projectMap = {};
    if (projects && projects.length > 0) {
      for (let i = 0; i < projects.length; i++) {
        projectMap[projects[i].id] = projects[i];
      }
    }
    
    for (let i = 0; i < resources.length; i++) {
      const resource = resources[i];
      data[resource.id] = {};
      for (let j = 0; j < weeks.length; j++) {
        const weekKey = weeks[j];
        data[resource.id][weekKey] = { hours: 0, taskNames: [] };
      }
    }

    for (let t = 0; t < tasks.length; t++) {
      const task = tasks[t];
      if (!task.startDate || !task.endDate) continue;
      const taskWeeks = getWeeksBetween(task.startDate, task.endDate);
      const allowedWeek = {};
      for (let w = 0; w < taskWeeks.length; w++) {
        allowedWeek[taskWeeks[w]] = true;
      }
      const allocations = task.resourceAllocations || [];
      const projectName = projectMap[task.projectId] ? projectMap[task.projectId].name : 'Senza progetto';
      for (let a = 0; a < allocations.length; a++) {
        const allocation = allocations[a];
        const resourceId = allocation.resourceId;
        if (!data[resourceId]) continue;
        
        const weeklyHours = allocation.weeklyHours || {};
        const weekKeys = Object.keys(weeklyHours);
        for (let w = 0; w < weekKeys.length; w++) {
          const weekKey = weekKeys[w];
          if (!allowedWeek[weekKey]) continue;
          const hours = weeklyHours[weekKey];
          if (data[resourceId][weekKey]) {
            data[resourceId][weekKey].hours += hours;
            data[resourceId][weekKey].taskNames.push(task.name + ' (' + projectName + '): ' + hours + 'h');
          }
        }
      }
    }

    return data;
  }, [resources, tasks, projects, weeks]);

  const getInitials = function(name) {
    const parts = name.split(' ');
    let result = '';
    for (let i = 0; i < parts.length; i++) {
      result += parts[i][0];
    }
    return result.toUpperCase();
  };

  const weekHeaders = [];
  for (let i = 0; i < weeks.length; i++) {
    const weekKey = weeks[i];
    const dateRange = getWeekDateRange(weekKey);
    weekHeaders.push(
      <div 
        key={weekKey}
        className={cn(
          "text-xs font-medium py-2 text-center border-r border-border/30 flex-shrink-0 leading-tight",
          i === currentWeekIndex && "border-l-2 border-r-2 border-red-500"
        )}
        style={{ width: CELL_WIDTH }}
        title={weekKey}
      >
        <div>{dateRange.start}</div>
        <div className="text-muted-foreground">{dateRange.end}</div>
      </div>
    );
  }

  const resourceRows = [];
  for (let i = 0; i < sortedResources.length; i++) {
    const resource = sortedResources[i];
    resourceRows.push(
      <div 
        key={resource.id}
        className="flex items-center gap-3 px-4 border-b border-border/50 hover:bg-muted/30 transition-colors cursor-pointer"
        style={{ height: ROW_HEIGHT }}
        data-testid={'resource-row-' + resource.id}
        onClick={function() { if (onResourceClick) onResourceClick(resource); }}
      >
        <Avatar className="h-8 w-8">
          <AvatarImage src={resource.avatar} alt={resource.name} />
          <AvatarFallback className="text-xs bg-primary/10">
            {getInitials(resource.name)}
          </AvatarFallback>
        </Avatar>
        <span className="text-sm truncate font-medium">{resource.name}</span>
      </div>
    );
  }

  const gridRows = [];
  for (let i = 0; i < sortedResources.length; i++) {
    const resource = sortedResources[i];
    const cells = [];
    
    for (let j = 0; j < weeks.length; j++) {
      const weekKey = weeks[j];
      const cellData = workloadData[resource.id] ? workloadData[resource.id][weekKey] : null;
      const taskHours = cellData ? cellData.hours : 0;
      const holidayHours = (holidayData[resource.id] && holidayData[resource.id][weekKey]) || 0;
      const totalHours = taskHours + holidayHours;
      const workingDays = hoursToWorkingDays(taskHours);
      const isOverloaded = totalHours > MAX_WORKING_DAYS * 8;
      const hasLoad = taskHours > 0;
      const hasHoliday = holidayHours > 0;

      let tooltipParts = [];
      if (hasLoad) tooltipParts = tooltipParts.concat(cellData.taskNames);
      if (hasHoliday) tooltipParts.push('Ferie: ' + holidayHours + 'h');
      if (hasLoad || hasHoliday) tooltipParts.push('Totale occupato: ' + totalHours + 'h / 40h');

      cells.push(
        <div
          key={weekKey}
          className={cn(
            "flex items-center justify-center border-r border-border/20 transition-colors flex-shrink-0",
            isOverloaded && "bg-destructive/20",
            !isOverloaded && hasLoad && "bg-primary/10",
            !isOverloaded && !hasLoad && hasHoliday && "bg-green-500/15",
            j === currentWeekIndex && "border-l-2 border-r-2 border-red-500"
          )}
          style={{ width: CELL_WIDTH, height: '100%' }}
          data-testid={'workload-cell-' + resource.id + '-' + weekKey}
          title={tooltipParts.join('\n')}
        >
          {hasLoad && (
            <span
              className={cn(
                "text-sm font-semibold",
                isOverloaded ? "text-destructive" : "text-foreground"
              )}
            >
              {workingDays.toFixed(1)}
            </span>
          )}
          {!hasLoad && hasHoliday && (
            <span className={cn("text-xs font-medium", isOverloaded ? "text-destructive" : "text-green-600 dark:text-green-400")}>
              {hoursToWorkingDays(holidayHours).toFixed(1)}f
            </span>
          )}
        </div>
      );
    }
    
    gridRows.push(
      <div 
        key={'grid-' + resource.id}
        className="flex border-b border-border/30"
        style={{ height: ROW_HEIGHT, width: weeks.length * CELL_WIDTH }}
      >
        {cells}
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full border border-border rounded-lg overflow-hidden bg-card">
      {/* Header */}
      <div className="flex border-b border-border bg-muted/30 flex-shrink-0">
        <div 
          className="flex-shrink-0 border-r border-border px-4 py-3 font-semibold text-sm"
          style={{ width: sidebarWidth }}
        >
          Risorse
        </div>
        <div
          className="flex-shrink-0 w-2 cursor-col-resize bg-border/60 hover:bg-border"
          onMouseDown={handleResizeStart}
          aria-label="Ridimensiona colonna risorse"
        />
        
        <div 
          ref={headerScrollRef}
          className="flex-1 overflow-x-auto"
          onScroll={function() { syncScrollLeft(headerScrollRef, gridScrollRef); }}
        >
          <div className="flex" style={{ width: weeks.length * CELL_WIDTH }}>
            {weekHeaders}
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="flex flex-1 overflow-hidden">
        <div 
          ref={sidebarScrollRef}
          className="flex-shrink-0 border-r border-border overflow-y-auto scrollbar-hide"
          style={{ width: sidebarWidth }}
          onScroll={function() { syncScrollTop(sidebarScrollRef, gridScrollRef); }}
        >
          {resourceRows}
        </div>
        <div
          className="flex-shrink-0 w-2 cursor-col-resize bg-border/60 hover:bg-border"
          onMouseDown={handleResizeStart}
        />

        <div 
          ref={gridScrollRef}
          className="flex-1 overflow-auto"
          onScroll={function() { 
            syncScrollTop(gridScrollRef, sidebarScrollRef);
            syncScrollLeft(gridScrollRef, headerScrollRef);
          }}
        >
          <div style={{ width: weeks.length * CELL_WIDTH }}>
            {gridRows}
          </div>
        </div>
      </div>

      {/* Legend */}
      <div className="flex items-center gap-6 px-4 py-2 border-t border-border bg-muted/20 text-xs flex-shrink-0">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-primary/10 border border-border" />
          <span className="text-muted-foreground">Carico normale</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-green-500/15 border border-border" />
          <span className="text-muted-foreground">Solo ferie</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-destructive/20 border border-border" />
          <span className="text-muted-foreground">Sovraccarico (carichi + ferie &gt;40h)</span>
        </div>
        <div className="ml-auto text-muted-foreground">
          Valori in giorni lavorativi (8h/giorno) • Clicca su una risorsa per modificarla
        </div>
      </div>
    </div>
  );
};

export default ResourceGrid;
